import React, {useMemo, useState} from 'react';
import MediaQuery from 'react-responsive';
import './styles.css';
import {CSSTransition} from 'react-transition-group';
import FiltersContentLoader from '../../components/FiltersContentLoader';
import GridHistoryWithFilters from '../../components/GridHistoryWithFilters';
import HorizontalLoader from '../../components/HorizontalLoader';
import ListSearch from '../../components/ListSearch';
import ListSearchContentLoader from '../../components/ListSearchContentLoader';
import NoEventsMask from '../../components/NoEventsMask';
import {ANIMATION_TIMEOUT_APPEAR} from '../../constants/animation';
import ENDPOINT from '../../constants/endpoints';
import {NetworkActions} from '../../helpers/actions/network';
import {updateHistory} from '../../helpers/grid-history';
import useApi from '../../hooks/useApi';
import {useGrid} from '../../hooks/useGrid';
import {useGridFilters} from '../../hooks/useGridFilters';
import {useSelection} from '../../hooks/useSelection';
import PageLayout from '../../layouts/PageLayout';
import FiltersAccordion from '../../scenes/FiltersAccordion';
import FiltersWidget from '../../scenes/FiltersAccordion/components/FiltersWidget';
import List from '../../scenes/List';
import NetworkItem from '../../scenes/List/components/NetworkItem';
import Sidebar from '../../scenes/Sidebar';
import FiltersMobileToggler from '../../scenes/Sidebar/components/FiltersMobileToggler';
import WidgetOnMobile from '../../scenes/Sidebar/components/WidgetOnMobile';

const MyNetwork = props => {
  const { user, history } = props;

  const api = useApi();

  const filters = useGridFilters(ENDPOINT.FILTERS.MY_NETWORK);

  const grid = useGrid(ENDPOINT.COLLECTION.MY_NETWORK, params => {
    updateHistory(history, params);
  });

  const [
    { isSelected, selectAll, deselectAll, select, deselect, getSelectedIds },
  ] = useSelection(grid.data.ids);

  const [mobileFilters, toggleMobileFilters] = useState(false);

  const toggleMobileFiltersHandler = () => {
    toggleMobileFilters(!mobileFilters);
  };

  const sortControls = [
    {
      defaultCaption: 'All',
      toggledCaption: 'All',
      value: { opt: 0 },
    },
    {
      defaultCaption: 'Firm Alumni',
      toggledCaption: 'Firm Alumni',
      value: { opt: 2 },
    },
    {
      defaultCaption: 'School Alumni',
      toggledCaption: 'School Alumni',
      value: { opt: 3 },
    },
  ];

  const filtersAccordion = useMemo(
    () =>
      filters.data && (
        <FiltersAccordion
          title={'Search Network'}
          subTitle={'Use filters below to filter your connections.'}
          filters={filters.data}
          onChange={filters.actions.change}
          onGroupSelectAll={filters.actions.groupSelect}
          onGroupClearAll={filters.actions.groupClear}
          onClearAll={filters.actions.clear}
          onSearch={filters.actions.search}
          onPushValue={filters.actions.push}
          render={renderProps => <FiltersWidget {...renderProps} />}
        />
      ),
    [filters.data]
  );

  const handleOnSelectAll = checked => {
    checked ? selectAll() : deselectAll();
  };

  const sendMassMessage = () => {
    const action = new NetworkActions(api);
    action.sendMessages(getSelectedIds());
  };

  const actionSelectControls = [
    {
      caption: 'Select All',
      icon: 'tick',
      iconActive: 'cross',
      controlFunc: handleOnSelectAll,
    },
  ];

  const actionButtonControls = [
    {
      caption: 'Message',
      controlFunc: sendMassMessage,
    },
  ];

  return (
    <GridHistoryWithFilters
      history={history}
      grid={grid}
      filters={filters}
      initialParams={{ opt: 0 }}
    >
      <PageLayout
        pageClass="network-page"
        pageTitle="My Network"
        mainContent={
          <>
            {grid.data.init ? (
              <ListSearch
                recordsCount={grid.data.rowsCount}
                recordsName={
                  grid.data.rowsCount === 1 ? 'Connection' : 'Connections'
                }
                search={grid.data.params.search || false}
                sortBy={
                  grid.data.params.opt
                    ? { opt: grid.data.params.opt }
                    : { opt: 0 }
                }
                sortControls={sortControls}
                actionSelectControls={actionSelectControls}
                actionButtonControls={actionButtonControls}
                searchPlaceholder="Search network"
                onChangeSortByHandler={grid.actions.sortBy}
                onSendSearchQueryHandler={grid.actions.search}
                isFetching={grid.data.isFetching}
                renderHeader={
                  <MediaQuery query="(max-width: 767px)">
                    <FiltersMobileToggler
                      title="Search Filters:"
                      toggleFunc={toggleMobileFiltersHandler}
                    />
                  </MediaQuery>
                }
              />
            ) : (
              <ListSearchContentLoader />
            )}
            <div className="horizontal-loader__container">
              {grid.data.isFetching && <HorizontalLoader />}
            </div>
            <List
              items={grid.data.rows}
              itemRender={(item, actions) => (
                <NetworkItem
                  user={user}
                  key={item.CandidateID}
                  item={item}
                  hasSelectMarker={true}
                  actions={actions}
                  selected={isSelected(item.CandidateID)}
                />
              )}
              selectable
              actions={{
                onSelect: select,
                onDeselect: deselect,
                onChangePage: grid.actions.readPage,
              }}
              pagination={{
                page: grid.data.page,
                count: grid.data.rowsCount,
                perPage: grid.data.perPage,
              }}
            />
          </>
        }
        sidebarContent={
          grid.data.init ? (
            <Sidebar>
              {grid.data.isFetching && (
                <CSSTransition
                  in={grid.data.init}
                  appear={true}
                  timeout={ANIMATION_TIMEOUT_APPEAR}
                  classNames="fade"
                >
                  <NoEventsMask relative={true} />
                </CSSTransition>
              )}
              <WidgetOnMobile
                renderComponent={filtersAccordion}
                status={mobileFilters}
                toggleFunc={toggleMobileFiltersHandler}
              />
            </Sidebar>
          ) : (
            <FiltersContentLoader />
          )
        }
      />
    </GridHistoryWithFilters>
  );
};

export default MyNetwork;
